import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';

import LanguageSelect from 'components/LanguageSelect';
import TokenService from 'utils/axios/token.axios';

// eslint-disable-next-line import/no-cycle
import { removeLocalStorage } from 'utils/func';
import instance from 'utils/axios/instance.axios';
import { LayoutContext } from './context/layoutcontext';
import AppTopbarMenu from './AppTopbarMenu';

const AppTopbar = forwardRef((props, ref) => {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 991);

  const { layoutState, onMenuToggle, showProfileSidebar } = useContext(LayoutContext);
  const user = useMemo(() => {
    const userLocal = localStorage.getItem('admin-user');
    return userLocal ? JSON.parse(userLocal) : null;
  });

  const menubuttonRef = useRef(null);
  const topbarmenuRef = useRef(null);
  const topbarmenubuttonRef = useRef(null);
  const settingRef = useRef(null);
  const profileRef = useRef(null);

  // #endregion Data

  // #region Event
  useImperativeHandle(ref, () => ({
    menubutton: menubuttonRef.current,
    topbarmenu: topbarmenuRef.current,
    topbarmenubutton: topbarmenubuttonRef.current,
  }));

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 991);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showSettingModal = (event) => {
    settingRef.current.toggle(event);
  };
  const showProfileModal = (event) => {
    profileRef.current.toggle(event);
  };
  const handleLogout = async () => {
    try {
      await instance.post('/users/logout');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    TokenService.removeUser();
    removeLocalStorage();
    navigate('/login');
  };
  // #endregion Event
  return (
    <div className="layout-topbar">
      {!isDesktop && (
        <button
          ref={menubuttonRef}
          type="button"
          className="p-link layout-menu-button layout-topbar-button"
          onClick={onMenuToggle}
        >
          <i className="pi pi-bars" />
        </button>
      )}

      {!isDesktop && (
        <Link to="/" className="layout-topbar-logo">
          <span className="text-xl sm:text-2xl">{t('websiteName')}</span>
        </Link>
      )}

      {isDesktop && (
        <Link to="/" className="layout-topbar-logo">
          <div className="logo-wrapper">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="VNU-HCM logo"
              className="ml-6 h-3rem md:h-4rem"
            />
          </div>
        </Link>
      )}

      {isDesktop && <AppTopbarMenu />}

      <button
        ref={topbarmenubuttonRef}
        type="button"
        className="p-link layout-topbar-menu-button layout-topbar-button"
        onClick={showProfileSidebar}
      >
        <i className="pi pi-ellipsis-v" />
      </button>

      <div
        ref={topbarmenuRef}
        className={classNames('layout-topbar-menu', {
          'layout-topbar-menu-mobile-active': layoutState.profileSidebarVisible,
        })}
      >
        {user ? (
          <button type="button" className="p-link layout-topbar-button" onClick={showProfileModal}>
            <i className="pi pi-user" />
            <span>{t('topbar.profile')}</span>
            <OverlayPanel
              ref={profileRef}
              appendTo={typeof window !== 'undefined' ? document.body : null}
              showCloseIcon={false}
              id="overlay_panel_profile"
            >
              <div className="flex align-items-center p-3">
                <i className="pi pi-user text-2xl" />
                <div className="ml-4">
                  <h6 className="m-0 text-lg">{user.fullname}</h6>
                  <p>
                    {user.email.split('@')[0]}
                    <wbr />@{user.email.split('@')[1]}
                  </p>
                </div>
              </div>
              <hr className="m-0 bg-gray-300" style={{ height: 0.5 }} />
              <ul className="profile-menu list-none p-0 m-0">
                <li>
                  <Link to="/me" className="text-color py-3">
                    <i className="pi pi-user mr-3" />
                    {t('topbar.profile')}
                  </Link>
                </li>
                <hr className="m-0 bg-gray-300" style={{ height: 0.5 }} />
                <li>
                  <Link to="/update-password" className="text-color py-3">
                    <i className="pi pi-key mr-3" />
                    {t('topbar.updatePassword')}
                  </Link>
                </li>
                <hr className="m-0 bg-gray-300" style={{ height: 0.5 }} />
                <span onClick={handleLogout} className="py-3">
                  <i className="pi pi-sign-out mr-3" />
                  {t('topbar.logout')}
                </span>
              </ul>
            </OverlayPanel>
          </button>
        ) : (
          <Link to="/login">
            <Button
              label={t('login.login')}
              outlined
              className="w-full lg:w-auto text-black lg:text-white"
            />
          </Link>
        )}

        <button type="button" className="p-link layout-topbar-button" onClick={showSettingModal}>
          <i className="pi pi-cog" />
          <span>{t('topbar.setting')}</span>
        </button>
        <OverlayPanel
          ref={settingRef}
          appendTo={typeof window !== 'undefined' ? document.body : null}
          showCloseIcon={false}
          id="overlay_panel_setting"
          style={{ width: '200px' }}
        >
          <LanguageSelect />
        </OverlayPanel>
      </div>
    </div>
  );
});

export default AppTopbar;
